const PKCE_VERIFIER_KEY = 'pkce_code_verifier'
const STATE_KEY = 'oauth_state'
const NONCE_KEY = 'oauth_nonce'

const randomUrlSafe = (byteLength = 32): string => {
  const array = new Uint8Array(byteLength)
  crypto.getRandomValues(array)
  return btoa(String.fromCharCode(...array))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '')
}

const generateCodeChallenge = async (verifier: string): Promise<string> => {
  const encoder = new TextEncoder()
  const data = encoder.encode(verifier)
  const digest = await crypto.subtle.digest('SHA-256', data)
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '')
}

const peek = (key: string): string | null => sessionStorage.getItem(key)

export const goToAuthPortal = async () => {
  const codeVerifier = randomUrlSafe()
  const codeChallenge = await generateCodeChallenge(codeVerifier)
  const state = randomUrlSafe(16)
  const nonce = randomUrlSafe(16)

  sessionStorage.setItem(PKCE_VERIFIER_KEY, codeVerifier)
  sessionStorage.setItem(STATE_KEY, state)
  sessionStorage.setItem(NONCE_KEY, nonce)

  const params = new URLSearchParams({
    client_id: import.meta.env.VITE_OAUTH_CLIENT_ID,
    redirect_uri: window.location.origin,
    response_type: 'code',
    scope: 'openid email',
    state,
    nonce,
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
  })

  window.location.href = `${import.meta.env.VITE_OAUTH_URL}/oauth/authorize?${params}`
}

// OpenID Connect RP-initiated logout (single sign-out): ends the shared Account
// Center SSO session, then returns to the sign-in page.
// `post_logout_redirect_uri` must be registered on the OAuth app in the Account
// Center back-office, otherwise it is ignored.
export const goToLogoutPortal = () => {
  const params = new URLSearchParams({
    client_id: import.meta.env.VITE_OAUTH_CLIENT_ID,
    post_logout_redirect_uri: `${window.location.origin}/sign-in`,
  })

  window.location.href = `${import.meta.env.VITE_OAUTH_URL}/oauth/logout?${params}`
}

export const getStoredCodeVerifier = (): string | null => peek(PKCE_VERIFIER_KEY)

export const getStoredState = (): string | null => peek(STATE_KEY)

export const getStoredNonce = (): string | null => peek(NONCE_KEY)

// Clears the single-use PKCE/CSRF material. Call only once the exchange has
// succeeded (or when deliberately restarting the flow), never merely on read —
// wiping it mid-flight forces a pointless re-authorization round-trip.
export const clearOauthState = () => {
  sessionStorage.removeItem(PKCE_VERIFIER_KEY)
  sessionStorage.removeItem(STATE_KEY)
  sessionStorage.removeItem(NONCE_KEY)
}
